// PRD.318 — PowerSync queries for Dental Treatment Plan & Treatment Done.
//
// Split from `dental-treatment-logic.ts` (lifecycle, selection, suggestions)
// because that module must stay import-free to run under the node:test harness.

import type { QuerySpec } from '@helios/powersync-client-core';

export * from './dental-treatment-logic.js';

/**
 * Every dental treatment plan item for a patient.
 *
 * PATIENT-scoped, not encounter-scoped: a plan raised in August is still active
 * at September's visit. Status filtering happens in `selectActivePlan`.
 */
export function qDentalTreatmentsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatments', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatments WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatments'],
  };
}

/** Planned tooth scope for all of a patient's treatments, grouped client-side. */
export function qDentalTreatmentTeethByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_teeth', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_teeth WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_teeth'],
  };
}

/**
 * Every performed record for a patient, across encounters.
 *
 * Patient-wide so one plan row can show its full multi-visit history; the
 * Treatment Done view narrows to the active encounter in the UI.
 */
export function qDentalTreatmentRecordsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_records', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_records WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_records'],
  };
}

/** Performed tooth scope for all of a patient's records. */
export function qDentalTreatmentRecordTeethByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['dental_treatment_record_teeth', 'by-patient', patientId] as const,
    sql: `SELECT * FROM dental_treatment_record_teeth WHERE patient_id = ?`,
    params: [patientId],
    watch: ['dental_treatment_record_teeth'],
  };
}
